import React from "react";
import moment from "moment-timezone";

import { IColum } from "../../utils/copyToClipboardInXlsFormat";
import { DATE_FORMAT, HOURS_12_FORMAT_WITH_DATE } from "../../constants";
import { RenderCellValue, replaceCommaAndNewLineSymbols, replaceNewLineAndTrim } from "./utils";
import { CopyToClipboard } from "../../components/ui";
import { IntlFormatUSD } from "../../utils";
import EntriesIdWithPrefix, {
  getEntriesIdWithPrefix,
} from "../FundedCompliance/components/EntriesIdWithPrefix";
import { IGetPassesResponse } from "../../api/compliance/index.types";

export const PASSES_COLUMNS: Array<IColum> = [
  {
    by: "timestamp",
    title: "Timestamp",
    path: "timestamp",
    copyToClipBoardCallBack: (value: string | number | Date) => moment(value).format(DATE_FORMAT),
    getCellValue: (value) => moment(value as string).format(DATE_FORMAT),
  },
  {
    by: "name",
    title: "Name",
    path: "account.name",
    // @ts-ignore
    getCellValue: (
      value: string,
      resetValues: IGetPassesResponse["data"]["results"]["rows"][number]
    ) => (
      <CopyToClipboard
        value={
          value ||
          resetValues?.submissions_outer_account?.name ||
          resetValues["submissions_outer_account.name"] ||
          ""
        }
      />
    ),
    // @ts-ignore
    copyToClipBoardCallBack: (
      value: string,
      resetValues: IGetPassesResponse["data"]["results"]["rows"][number]
    ) => {
      return value || resetValues?.submissions_outer_account?.name || "";
    },
  },
  {
    by: "email",
    title: "Email",
    path: "email",
    getCellValue: (value) => <CopyToClipboard value={value as unknown as string} />,
  },
  {
    by: "eval_account_name",
    title: "Passed evaluation account",
    path: "e2t_account_id",
  },
  {
    by: "eval_account_size",
    title: "Account size",
    path: "type_and_size",
  },
];

export const HELIOS_LIVE_SIM_TIF_COLUMNS: Array<IColum> = [
  {
    by: "timestamp",
    title: "Submission timestamp",
    path: "timestamp",
    getCellValue: (value) => <>{moment(value as string).format(HOURS_12_FORMAT_WITH_DATE)}</>,
    copyToClipBoardCallBack: (value) => moment(value as string).format(HOURS_12_FORMAT_WITH_DATE),
  },
  {
    by: "pw",
    title: "PW",
    path: "traded_account.e2t_account_id",
    getCellValue: (value) => <CopyToClipboard value={value as unknown as string} />,
    copyToClipBoardCallBack: (value) => replaceCommaAndNewLineSymbols(value),
  },
  {
    by: "name",
    title: "Name",
    path: "name",
    getCellValue: (value) => <CopyToClipboard value={value as unknown as string} />,
  },
  {
    by: "email",
    title: "Email",
    path: "email",
    getCellValue: (value) => <CopyToClipboard value={value as unknown as string} />,
  },
  {
    by: "status",
    title: "Status",
    path: "traded_account.processing_status",
  },
  {
    by: "dob",
    title: "DOB",
    path: "dob",
  },
  {
    by: "phone",
    title: "Phone",
    path: "phone",
    copyToClipBoardCallBack: (value: string): string => {
      if (value) {
        return value.replaceAll("+", "").replaceAll("\n", " ");
      }
      return "";
    },
    getCellValue: (value) => <CopyToClipboard value={value as unknown as string} />,
  },
  {
    by: "address",
    title: "Address",
    path: "address",
    copyToClipBoardCallBack: (value) => replaceCommaAndNewLineSymbols(value),
    getCellValue: (value) => <CopyToClipboard value={value as unknown as string} />,
  },
  {
    by: "account_type",
    title: "Account type",
    path: "traded_account.account_type",
  },
  {
    by: "id_and_poa",
    title: "ID and POA",
    path: "address_proof",
    getCellValue: (value) => <RenderCellValue value={value as unknown as string} />,
    copyToClipBoardCallBack: (value) => replaceCommaAndNewLineSymbols(value),
  },
  {
    by: "tax_form",
    title: "Tax form",
    path: "tax_form",
    getCellValue: (value) => <RenderCellValue value={value as unknown as string} />,
    copyToClipBoardCallBack: (value) => replaceCommaAndNewLineSymbols(value),
  },
];

export const HELIOS_LIVE_TIF_COLUMNS: Array<IColum> = [
  {
    by: "submission_timestamp",
    title: "Submission timestamp",
    path: "timestamp",
    copyToClipBoardCallBack: (value) => replaceNewLineAndTrim(value),
  },
  {
    by: "name",
    title: "Name",
    path: "name",
    copyToClipBoardCallBack: (value) => replaceNewLineAndTrim(value),
    getCellValue: (value) => <CopyToClipboard value={value as unknown as string} />,
  },
  {
    by: "email",
    title: "Email",
    path: "email",
    copyToClipBoardCallBack: (value) => replaceNewLineAndTrim(value),
    getCellValue: (value) => <CopyToClipboard value={value as unknown as string} />,
  },
  {
    by: "status",
    title: "Status",
    path: "traded_account.processing_status",
    copyToClipBoardCallBack: (value) => replaceNewLineAndTrim(value),
  },
  {
    by: "dob",
    title: "DOB",
    path: "dob",
    copyToClipBoardCallBack: (value) => replaceNewLineAndTrim(value),
  },
  {
    by: "phone",
    title: "Phone",
    path: "phone",
    copyToClipBoardCallBack: (value: string): string => {
      if (value) {
        return value.replaceAll("+", "").replaceAll("\n", " ");
      }
      return "";
    },
    getCellValue: (value) => <CopyToClipboard value={value as unknown as string} />,
  },
  {
    by: "address",
    title: "Address",
    path: "address",
    copyToClipBoardCallBack: (value) => replaceCommaAndNewLineSymbols(value),
    getCellValue: (value) => <CopyToClipboard value={value as unknown as string} />,
  },
  {
    by: "account_type",
    title: "Account type",
    path: "traded_account.account_type",
    copyToClipBoardCallBack: (value) => replaceCommaAndNewLineSymbols(value),
  },
  {
    by: "id_and_poa",
    title: "ID and POA",
    path: "id_or_passport",
    getCellValue: (value) => <RenderCellValue value={value as unknown as string} />,
    copyToClipBoardCallBack: (value) => replaceCommaAndNewLineSymbols(value),
  },
  {
    by: "tax_form",
    title: "Tax form",
    path: "tax_form",
    getCellValue: (value) => <RenderCellValue value={value as unknown as string} />,
    copyToClipBoardCallBack: (value) => replaceCommaAndNewLineSymbols(value),
  },
];

export const HELIOS_AND_APPIUS_LIVE_TIF_COLUMNS: Array<IColum> = [
  {
    by: "compliance_entries_id",
    title: "Entries ID",
    path: "compliance_traders_account.compliance_entries_id",
  },
  {
    by: "timestamp",
    title: "Submission timestamp",
    path: "timestamp",
    getCellValue: (value) => <>{moment(value as string).format(HOURS_12_FORMAT_WITH_DATE)}</>,
    copyToClipBoardCallBack: (value) => moment(value as string).format(HOURS_12_FORMAT_WITH_DATE),
    // copyToClipBoardCallBack: (value) => replaceNewLineAndTrim(value),
  },
  {
    by: "account_credentials",
    title: "Account credentials",
    path: "traded_account.e2t_account_id",
  },
  {
    by: "broker",
    title: "Broker",
    path: "trading_platform",
  },
  {
    by: "name",
    title: "Name",
    path: "name",
    copyToClipBoardCallBack: (value) => replaceNewLineAndTrim(value),
    getCellValue: (value) => <CopyToClipboard value={value as unknown as string} />,
  },
  {
    by: "email",
    title: "Email",
    path: "email",
    copyToClipBoardCallBack: (value) => replaceNewLineAndTrim(value),
    getCellValue: (value) => <CopyToClipboard value={value as unknown as string} />,
  },
  {
    by: "status",
    title: "Status",
    path: "traded_account.processing_status",
    copyToClipBoardCallBack: (value) => replaceNewLineAndTrim(value),
  },
  {
    by: "dob",
    title: "DOB",
    path: "dob",
    copyToClipBoardCallBack: (value) => replaceNewLineAndTrim(value),
  },
  {
    by: "phone",
    title: "Phone",
    path: "phone",
    copyToClipBoardCallBack: (value: string): string => {
      if (value) {
        return value.replaceAll("+", "").replaceAll("\n", " ");
      }
      return "";
    },
  },
  {
    by: "address",
    title: "Address",
    path: "address",
    copyToClipBoardCallBack: (value) => replaceCommaAndNewLineSymbols(value),
  },
  {
    by: "account_type",
    title: "Account type",
    path: "traded_account.account_type",
    copyToClipBoardCallBack: (value) => replaceCommaAndNewLineSymbols(value),
  },
  {
    by: "id_and_poa",
    title: "ID and POA",
    path: "id_or_passport",
    getCellValue: (value) => <RenderCellValue value={value as unknown as string} />,
    copyToClipBoardCallBack: (value) => replaceCommaAndNewLineSymbols(value),
  },
  {
    by: "tax_form",
    title: "Tax form",
    path: "tax_form",
    getCellValue: (value) => <RenderCellValue value={value as unknown as string} />,
    copyToClipBoardCallBack: (value) => replaceCommaAndNewLineSymbols(value),
  },
];

export const GM_CAPPERS_COLUMNS: Array<IColum> = [
  {
    by: "timestamp",
    title: "Timestamp",
    path: "timestamp",
    copyToClipBoardCallBack: (value) => replaceNewLineAndTrim(value),
  },
  {
    by: "name",
    title: "Name",
    path: "name",
    copyToClipBoardCallBack: (value) => replaceNewLineAndTrim(value),
    getCellValue: (value) => <CopyToClipboard value={value as unknown as string} />,
  },
  {
    by: "email",
    title: "Email",
    path: "email",
    getCellValue: (value) => <CopyToClipboard value={value as unknown as string} />,
  },
  {
    by: "gauntlet_completed",
    title: "Gauntlet completed",
    path: "gauntlet_completed",
    getCellValue: (value) => <CopyToClipboard value={value as unknown as string} />,
  },
  {
    by: "completed_account_name_in_rithmic",
    title: "Capped account name",
    path: "completed_account_name_in_rithmic",
    getCellValue: (value) => <CopyToClipboard value={value as unknown as string} />,
  },
  {
    by: "compliance_check_complete",
    title: "Compliance check complete",
    path: "compliance_check_complete",
    getCellValue: (value) => <CopyToClipboard value={value as unknown as string} />,
  },
  {
    by: "creds_sent",
    title: "Creds Sent?",
    path: "creds_sent",
    getCellValue: (value) => <CopyToClipboard value={value as unknown as string} />,
  },
  {
    by: "existing_accounts",
    title: "Existing accounts",
    path: "existing_accounts",
    getCellValue: (value) => (
      <span style={{ whiteSpace: "break-spaces" }}>{value as unknown as string}</span>
    ),
  },
];

export const TCP_STEPS_COLUMNS: Array<IColum> = [
  {
    by: "timestamp",
    title: "Timestamp",
    path: "timestamp",
  },
  {
    by: "name",
    title: "Name",
    path: "name",
    getCellValue: (value) => <CopyToClipboard value={value as unknown as string} />,
  },
  {
    by: "recent_email",
    title: "Recent email",
    path: "recent_email",
    getCellValue: (value) => <CopyToClipboard value={value as unknown as string} />,
  },
  {
    by: "starting_tcp_size",
    title: "Starting TCP size",
    path: "starting_tcp_size",
  },
  {
    by: "size_completed",
    title: "Size completed",
    path: "size_completed",
  },
  {
    by: "completed_account_name_in_rithmic",
    title: "Stepped account",
    path: "completed_account_name_in_rithmic",
  },
  {
    by: "compliance_check_complete",
    title: "Compliance check",
    path: "compliance_check_complete",
  },
  {
    by: "creds_sent",
    title: "Creds sent",
    path: "creds_sent",
  },
  {
    by: "existing_accounts",
    title: "Existing accounts",
    path: "existing_accounts",
  },
];

export const TOP_LIVE_LOSERS_COLUMNS: Array<IColum> = [
  {
    by: "compliance_entries_id",
    title: "Entries ID",
    path: "compliance_entries_id",
    getCellValue: (value) => <EntriesIdWithPrefix entriesId={value as unknown as number} />,
    copyToClipBoardCallBack: (value) => getEntriesIdWithPrefix(value as unknown as number),
  },
  {
    by: "name",
    title: "Name",
    path: "name",
    getCellValue: (value: unknown): React.ReactElement => {
      const stringValue = value as unknown as string;
      return (
        <>
          {stringValue?.split(", ").map((v) => (
            <CopyToClipboard key={v} value={v} />
          ))}
        </>
      );
    },
  },
  {
    by: "measure_taken",
    title: "Measure Taken",
    path: "measure_taken",
    getCellValue: (value: unknown): React.ReactElement => {
      const stringValue = value as unknown as string;
      return (
        <>
          {stringValue?.split("|| ").map((v) => (
            <span
              style={{
                display: "block",
                // @ts-ignore
                textWrap: "auto",
              }}
              key={v}
            >
              {v}
            </span>
          ))}
        </>
      );
    },
  },
  {
    by: "measure_timestamp",
    title: "Timestamp",
    path: "measure_timestamp",
    getCellValue: (value: unknown): React.ReactElement => {
      const stringValue = value as unknown as string;
      return (
        <>
          {stringValue?.split("|| ").map((v) => (
            <>
              {stringValue?.split("|| ").map((v) => (
                <span
                  style={{
                    display: "block",
                    // @ts-ignore
                    textWrap: "auto",
                  }}
                  key={v}
                >
                  {v}
                </span>
              ))}
            </>
          ))}
        </>
      );
    },
  },
  {
    by: "amount",
    title: "Amount",
    path: "live_losses_sum",
    getCellValue: (value) => <CopyToClipboard value={IntlFormatUSD(value as unknown as number)} />,
    copyToClipBoardCallBack: (value) => IntlFormatUSD(value as unknown as number),
  },
  {
    by: "total_pnl",
    title: "Total PnL",
    path: "pnl",
    getCellValue: (value, rowValues) => <CopyToClipboard value={IntlFormatUSD(+value)} />,
    copyToClipBoardCallBack: (value) => IntlFormatUSD(+value),
  },
];

export const TOP_LIVE_SIM_TAKERS: Array<IColum> = [
  {
    by: "compliance_entries_id",
    title: "Entries ID",
    path: "compliance_entries_id",
    getCellValue: (value) => <EntriesIdWithPrefix entriesId={value as unknown as number} />,
    copyToClipBoardCallBack: (value) => getEntriesIdWithPrefix(value as unknown as number),
  },
  {
    by: "name",
    title: "Name",
    path: "name",
    getCellValue: (value: unknown): React.ReactElement => {
      const stringValue = value as unknown as string;
      return (
        <>
          {stringValue?.split(", ").map((v) => (
            <CopyToClipboard key={v} value={v} />
          ))}
        </>
      );
    },
  },
  {
    by: "measure_taken",
    title: "Measure Taken",
    path: "measure_taken",
    getCellValue: (value: unknown): React.ReactElement => {
      const stringValue = value as unknown as string;
      return (
        <>
          {stringValue?.split("|| ").map((v) => (
            <span
              style={{
                display: "block",
                // @ts-ignore
                textWrap: "auto",
              }}
              key={v}
            >
              {v}
            </span>
          ))}
        </>
      );
    },
  },
  {
    by: "measure_timestamp",
    title: "Timestamp",
    path: "measure_timestamp",
    getCellValue: (value: unknown): React.ReactElement => {
      const stringValue = value as unknown as string;
      return (
        <>
          {stringValue?.split("|| ").map((v) => (
            <>
              {stringValue?.split("|| ").map((v) => (
                <span
                  style={{
                    display: "block",
                    // @ts-ignore
                    textWrap: "auto",
                  }}
                  key={v}
                >
                  {v}
                </span>
              ))}
            </>
          ))}
        </>
      );
    },
  },
  {
    by: "w80_sum",
    title: "Sum",
    path: "w80_sum",
    getCellValue: (value) => <CopyToClipboard value={IntlFormatUSD(value as unknown as number)} />,
  },
  {
    by: "total_pnl",
    title: "Total PnL",
    path: "pnl",
    getCellValue: (value, rowValues) => <CopyToClipboard value={IntlFormatUSD(+value)} />,
  },
];
